/**
 * ScheduleWorkflowCard.tsx
 * Individual workflow card for the Schedules dashboard grid.
 * Displays workflow name, status, trigger, action previews, and quick controls.
 */

import React, { useState } from 'react';
import { Icons, getAppIcon } from '../../../components/ui/Icons';
import { ScheduledWorkflow, WorkflowStatus } from '../../../types';
import { cronToHumanReadable } from '../../../services/scheduleParser';

interface ScheduleWorkflowCardProps {
  workflow: ScheduledWorkflow;
  onPauseResume: (id: string, newStatus: WorkflowStatus) => void;
  onDelete: (id: string) => void;
  onSelect: (workflow: ScheduledWorkflow, editMode?: boolean) => void;
}

const STATUS_CONFIG: Record<WorkflowStatus, { label: string; color: string; bg: string; border: string; dot: string }> = {
  active: { label: 'Active', color: 'text-black dark:text-white', bg: 'bg-gray-100 dark:bg-[#171717]', border: 'border-gray-200 dark:border-gray-800', dot: 'bg-black dark:bg-white' },
  paused: { label: 'Paused', color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-55 dark:bg-[#171717]/50', border: 'border-gray-200 dark:border-gray-800', dot: 'bg-gray-400 dark:bg-gray-600' },
  failed: { label: 'Failed', color: 'text-black font-semibold dark:text-white', bg: 'bg-gray-200 dark:bg-red-950/20', border: 'border-gray-300 dark:border-red-900/30', dot: 'bg-black dark:bg-red-400' },
};

const ScheduleWorkflowCard: React.FC<ScheduleWorkflowCardProps> = ({
  workflow,
  onPauseResume,
  onDelete,
  onSelect,
}) => {
  const statusConfig = STATUS_CONFIG[workflow.status];
  const triggerText = workflow.cron_expression
    ? cronToHumanReadable(workflow.cron_expression)
    : 'No schedule set';

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (Math.abs(diffHours) < 24) {
      if (diffHours === 0) return 'Now';
      if (diffHours > 0) return `In ${diffHours}h`;
      return `${Math.abs(diffHours)}h ago`;
    }
    if (Math.abs(diffDays) < 7) {
      if (diffDays > 0) return `In ${diffDays}d`;
      return `${Math.abs(diffDays)}d ago`;
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      onClick={() => onSelect(workflow, false)}
      className="bg-white dark:bg-[#212121] border border-gray-200 dark:border-gray-800 rounded-2xl p-5 hover-lift transition-all duration-300 group relative overflow-hidden cursor-pointer"
      id={`workflow-card-${workflow.id}`}
    >
      {/* Active indicator glow */}
      {workflow.status === 'active' && (
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-black dark:bg-white opacity-60"></div>
      )}

      {/* Header Row */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 dark:text-white text-base truncate pr-2">{workflow.name}</h3>
          {workflow.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{workflow.description}</p>
          )}
        </div>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${statusConfig.bg} ${statusConfig.border} border flex-shrink-0`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot} ${workflow.status === 'active' ? 'animate-pulse' : ''}`}></span>
          <span className={`text-[11px] font-bold ${statusConfig.color}`}>{statusConfig.label}</span>
        </div>
      </div>

      {/* Trigger Info */}
      <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-gray-50 dark:bg-[#171717] border border-gray-100 dark:border-gray-800">
        <Icons.Repeat className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{triggerText}</span>
      </div>

      {/* Action Steps Preview */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider flex-shrink-0">Steps:</span>
        <div className="flex items-center gap-1.5 overflow-hidden">
          {(workflow?.steps || []).slice(0, 4).map((step, i) => {
            const toolLow = step?.tool_name?.toLowerCase() || '';
            let iconId = 'default';
            if (toolLow.includes('email') || toolLow.includes('gmail')) iconId = 'gmail';
            else if (toolLow.includes('calendar') || toolLow.includes('event')) iconId = 'calendar';
            else if (toolLow.includes('sheet') || toolLow.includes('spreadsheet')) iconId = 'sheets';
            else if (toolLow.includes('doc')) iconId = 'docs';
            else if (toolLow.includes('drive') || toolLow.includes('file')) iconId = 'drive';
            else if (toolLow.includes('notion')) iconId = 'notion';
            const Icon = getAppIcon(iconId);

            return (
              <React.Fragment key={i}>
                <div className="w-7 h-7 rounded-lg bg-white dark:bg-[#171717] border border-gray-200 dark:border-gray-800 flex items-center justify-center shadow-sm flex-shrink-0" title={step?.tool_name}>
                  <Icon className="w-4 h-4 dark:text-gray-300" />
                </div>
                {i < Math.min((workflow?.steps || []).length - 1, 3) && (
                  <Icons.ArrowRight className="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                )}
              </React.Fragment>
            );
          })}
          {(workflow?.steps || []).length > 4 && (
            <span className="text-[11px] font-bold text-gray-400 dark:text-gray-500 ml-1">+{(workflow?.steps || []).length - 4}</span>
          )}
          {(workflow?.steps || []).length === 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">No actions</span>
          )}
        </div>
      </div>

      {/* Timing Footer */}
      <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-800 pt-3">
        <div className="flex items-center gap-3">
          {workflow.last_run_at && (
            <span>Last run: <strong className="text-gray-600 dark:text-gray-300">{formatDate(workflow.last_run_at)}</strong></span>
          )}
          {workflow.next_run_at && workflow.status === 'active' && (
            <span>Next: <strong className="text-gray-600 dark:text-gray-300">{formatDate(workflow.next_run_at)}</strong></span>
          )}
          {!workflow.last_run_at && !workflow.next_run_at && (
            <span>Created {formatDate(workflow.created_at)}</span>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {/* Pause/Resume */}
          <button
            onClick={(e) => { e.stopPropagation(); onPauseResume(workflow.id, workflow.status === 'active' ? 'paused' : 'active'); }}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#171717] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            title={workflow.status === 'active' ? 'Pause' : 'Resume'}
          >
            {workflow.status === 'active'
              ? <Icons.Pause className="w-3.5 h-3.5" />
              : <Icons.Play className="w-3.5 h-3.5" />
            }
          </button>

          {/* Edit */}
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(workflow, true); }}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#171717] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            title="Edit"
          >
            <Icons.Pencil className="w-3.5 h-3.5" />
          </button>

          {/* Delete */}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(workflow.id); }}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#171717] text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors"
            title="Delete"
          >
            <Icons.Trash className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleWorkflowCard;
